import React, { useMemo, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import BrandLogo from '@/components/brand/BrandLogo';
import UserAvatar, { userDisplayName } from '@/components/brand/UserAvatar';
import { useMemberApp } from '../AppProvider';
import {
  IconBell, IconBookmark, IconChart, IconChat, IconCrown, IconDoc, IconHeart, IconMic, IconPlus, IconSearch,
} from './Icons';
import { memberPath, talkUrl, wwwUrl } from '@/config/hosts';

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { conversations, isPro, setUpgradeOpen, setFeedbackOpen, sidebarOpen, setSidebarOpen, requirePro } = useMemberApp();
  const [menu, setMenu] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const navItems = useMemo(() => {
    const items = [
      { href: talkUrl('/'), label: 'Talk', icon: IconMic },
      { href: memberPath('/'), label: 'Type', icon: IconPlus, end: true },
      { href: memberPath('/check-ins'), label: 'Symptoms', icon: IconBell },
      { href: memberPath('/records'), label: 'Records', icon: IconDoc },
      { href: memberPath('/analysis'), label: 'Analysis', icon: IconChart, pro: true },
      { href: memberPath('/care'), label: 'Find care', icon: IconHeart },
      { href: memberPath('/community'), label: 'Community', icon: IconChat },
      { href: memberPath('/trials'), label: 'Clinical Trials', icon: IconSearch },
      { href: memberPath('/genomics'), label: 'Genomics', icon: IconChart },
    ];
    items.push({ href: memberPath('/admin'), label: 'Admin Panel', icon: IconCrown });
    return items;
  }, [user]);

  const recents = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (conversations || []).filter((c) => !q || (c.title || '').toLowerCase().includes(q)).slice(0, 16);
  }, [conversations, query]);

  const name = userDisplayName(user) === 'Account' ? 'There' : userDisplayName(user);
  const email = user?.email || '';

  const go = (href, pro) => (e) => {
    if (pro && !isPro) {
      e.preventDefault();
      requirePro();
      return;
    }
    setSidebarOpen(false);
    navigate(href);
  };

  return (
    <aside className={`by-sidebar${sidebarOpen ? ' is-open' : ''}`}>
      <BrandLogo
         className="by-brand"
        to={wwwUrl('/')}
        onClick={() => setSidebarOpen(false)}
      />

      <nav className="by-nav" aria-label="App">
        {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              className={({ isActive }) => `by-nav-item${isActive ? ' is-on' : ''}`}
              onClick={go(item.href, item.pro)}
            >
              <item.icon />
              {item.label}
              {item.pro && !isPro ? <span className="by-pro">PRO</span> : null}
            </NavLink>
        ))}
        {!isPro ? (
          <button type="button" className="by-nav-item" onClick={() => setUpgradeOpen(true)}>
            <IconCrown />
            Upgrade to Pro
          </button>
        ) : null}
      </nav>

      <div className="by-recents">
        <div className="by-recents-head">
          Recents
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconSearch />
          </span>
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          style={{
            margin: '0 8px 8px',
            border: '1px solid var(--by-line)',
            borderRadius: 10,
            padding: '8px 10px',
            background: '#fff',
            font: 'inherit',
            fontSize: 13,
          }}
        />
        <div className="by-recents-list">
          {recents.map((item) => (
            <NavLink
              key={item.id}
              to={memberPath(`/c/${item.id}`)}
              className={({ isActive }) => `by-recent-item${isActive ? ' is-on' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span>{item.title || 'Conversation'}</span>
            </NavLink>
          ))}
        </div>
      </div>

      <button type="button" className={`by-user${menu ? ' is-open' : ''}`} onClick={() => setMenu((v) => !v)}>
        <UserAvatar user={user} className="by-avatar" />
        <span className="by-user-meta">
          <b>{name}</b>
          <small>{email}</small>
        </span>
      </button>
      {menu ? (
        <div className="by-user-menu">
          <Link to={memberPath('/settings')} onClick={() => { setMenu(false); setSidebarOpen(false); }}>Settings</Link>
          <Link
            to={memberPath('/voice')}
            onClick={(e) => {
              setMenu(false);
              setSidebarOpen(false);
              if (!isPro) {
                e.preventDefault();
                requirePro();
              }
            }}
          >
            Voice {!isPro ? <span className="by-pro">PRO</span> : null}
          </Link>
          <Link to={memberPath('/account')} onClick={() => { setMenu(false); setSidebarOpen(false); }}>Account</Link>
          <button type="button" onClick={() => logout()}>Log out</button>
        </div>
      ) : null}
    </aside>
  );
}
